import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ModalComponent } from '../components/modal/modal.component';
import { Order } from '../models/order';
import { ErrorService } from './error.service';
import { SnackbarService } from './snackbar.service';
import { CustomPageService } from './custom-page.service';
import { UserService } from './user.service';

@Injectable()
export class OrderService {
	public availableOrderTypes: any[];
	public lastPlacedOrderId: any;
	public nextOrderAction: any;
	public order: Order;
	public orderQueue: any[] = [];
	public orderQueue$: BehaviorSubject<any>;
	public resetOrder$ = new EventEmitter();
	public unplacedOrderData: any;
	public nextOrderActionSubject = new Subject<any>();
	public nextOrderAction$: Observable<any> = this.nextOrderActionSubject.asObservable();
	private user: any;


	constructor(private _http: HttpClient,
				private _errorService: ErrorService,
				private _dialog: MatDialog,
				private _snackbarService: SnackbarService,
				private _customPageService: CustomPageService,
				private _userService: UserService) {
		this.orderQueue$ = new BehaviorSubject(this.orderQueue);
		this._userService.user$.subscribe((data) => {
			this.user = data;
		});
	}

	addOrderToQueue(order) {
		this.orderQueue.push(order);
		this.saveOrderQueue();
	}

	/**
	 * @api {put} /v2/Orders/Cancel/:id Cancel Order
	 * @apiName callCancelOrder
	 * @apiParam {boolean} [continuationBypass] Continuation Bypass
	 * @apiParam {boolean} [routeStopsBypass1] Route Stop Bypass 1
	 * @apiParam {boolean} [routeStopsBypass2] Route Stop Bypass 2
	 * @apiGroup Orders
	 * @apiVersion 20.0.0
	 */
	callCancelOrder(id, continuationBypass, routeStopsBypass1, routeStopsBypass2) {
		let params: HttpParams = new HttpParams();
		if (continuationBypass) {
			params = params.append('continuationBypass', continuationBypass);
		}
		if (routeStopsBypass1) {
			params = params.append('routeStopsBypass1', routeStopsBypass1);
		}
		if (routeStopsBypass2) {
			params = params.append('routeStopsBypass2', routeStopsBypass2);
		}
		return this._http.put<any>(`${environment.apiUrl}/v2/Orders/Cancel/${id}`, null, {params: params});
	}

	cancelOrder(id, continuationBypass, routeStopsBypass1, routeStopsBypass2) {
		return new Promise<void>((resolve, reject) => {
			this.callCancelOrder(id, continuationBypass, routeStopsBypass1, routeStopsBypass2).subscribe(() => {
				this._snackbarService.openSnackBar(`Order ${id} has been successfully cancelled!`, 'Close', 5000, 'success');
				resolve();
			}, (error) => {
				this._dialog.open(ModalComponent, {
					data: {
						title: 'Alert',
						message: `Order ${id} cannot be cancelled online.  Please contact customer service to cancel this order.`,
						showCancel: false
					}
				});
				this._errorService.error(error, 'Cancel order Failed');
				reject();
			});
		});
	}

	clearOrderQueue() {
		this.orderQueue = [];
		this.saveOrderQueue();
	}

	createDTOForOrder(order, final, selectedItems?, notificationEmails?, creditCard?) {
		if (final) {
			order.origin.originComments = this.prefixCommentsWithContact(order.origin.originComments, order.origin.contact, 'See');
			order.destination.destComments = this.prefixCommentsWithContact(order.destination.destComments, order.destination.contact, 'Attn');
		}

		order.caller = null;  // BE sets this value so leave as null
		order.emailPod = '';
		order.specialInst = '';

		if (notificationEmails && notificationEmails.length > 0) {
			let emailString = '';
			for (const i in notificationEmails) {
				if (emailString.length === 0) {
					emailString = notificationEmails[i];
				} else {
					emailString = emailString + ';' + notificationEmails[i];
				}
			}
			order.emailPod = emailString;
		}

		const orderObject = {
			order: null,
			accessorialItems: [],
			creditCard: creditCard && creditCard.number ? creditCard : null,
			orderTemplateId: null
		};

		if (selectedItems) {
			for (const i of Object.keys(selectedItems)) {
				const itemObjectDTO = {
					id: null,
					qty: null
				};
				itemObjectDTO.id = selectedItems[i].id;
				if (selectedItems[i].userEntryType === 'QUANTITY') {
					// We have to check if quantity is an empty string and if yes, change it back to a null.
					// This is required since we had to change the input type to text so we can apply formatting with commas.
					if (selectedItems[i].quantity === ''){
						selectedItems[i].quantity = null;
					}

					itemObjectDTO.qty = selectedItems[i].quantity ?? 0;
				} else {
					itemObjectDTO.qty = 1;
				}
				orderObject.accessorialItems.push(itemObjectDTO);
			}
		}

		orderObject.order = Object.assign({}, order);
		orderObject.orderTemplateId = order.orderTemplateId;

		if (orderObject.order.readyTimeFrom && orderObject.order.selectedPickupReady !== 'now') {
			orderObject.order.readyTimeFrom = this.getOffsetDifference(orderObject.order.readyTimeFrom, orderObject.order.pickupTimeOffset);
		} else if (orderObject.order.readyTimeFrom) {
			orderObject.order.readyTimeFrom = orderObject.order.readyTimeFrom;
		}

		if (orderObject.order.dueTimeFrom && orderObject.order.selectedDeliveryReady !== 'now' && orderObject.order.selectedPickupReady !== 'now') {
			orderObject.order.dueTimeFrom = this.getOffsetDifference(orderObject.order.dueTimeFrom, orderObject.order.deliveryTimeOffset);
		} else if (orderObject.order.dueTimeFrom) {
			orderObject.order.dueTimeFrom = orderObject.order.dueTimeFrom;
		}

		return orderObject;
	}

	/**
	 * @api {post} /v2/Orders Create Order
	 * @apiName createOrder
	 * @apiParam {boolean} [skipRating] Skip Rating
	 * @apiParam {boolean} [bypassInvoiceAgingWarning] Skip Invoice Aging Warning
	 * @apiGroup Orders
	 * @apiVersion 20.0.0
	 */
	createOrder(data, skipRating, isBasicOrder = false) {
		let params: HttpParams = new HttpParams();
		params = params.append('skipRating', skipRating);
		params = params.append('bypassInvoiceAgingWarning', true);
		params = params.append('isBasicOrder', isBasicOrder);
		return this._http.post<any>(`${environment.apiUrl}/v2/Orders`, data, {params});
	}

	createOrderDTO(final, selectedItems?, notificationEmails?, creditCard?) {
		if (final) {
			this.order.origin.originComments = this.prefixCommentsWithContact(this.order.origin.originComments, this.order.origin.contact, 'See');
			this.order.destination.destComments = this.prefixCommentsWithContact(this.order.destination.destComments, this.order.destination.contact, 'Attn');
		}

		this.order.caller = null;  // BE sets this value so leave as null
		this.order.emailPod = '';
		this.order.specialInst = '';

		if (notificationEmails && notificationEmails.length > 0) {
			let emailString = '';
			for (const i in notificationEmails) {
				if (emailString.length === 0) {
					emailString = notificationEmails[i];
				} else {
					emailString = emailString + ';' + notificationEmails[i];
				}
			}
			this.order.emailPod = emailString;
		}

		if (this.order.parcels && this.order.parcels.length > 0) {
			for (const i of Object.keys(this.order.parcels)) {
				this.order.parcels[i].type = 0;
			}
		}

		const orderObject = {
			order: null,
			accessorialItems: [],
			creditCard: creditCard && creditCard.number ? creditCard : null,
			orderTemplateId: null
		};

		if (selectedItems) {
			for (const i of Object.keys(selectedItems)) {
				const itemObjectDTO = {
					id: null,
					qty: null
				};
				itemObjectDTO.id = selectedItems[i].id;
				if (selectedItems[i].quantity) {
					itemObjectDTO.qty = selectedItems[i].quantity;
				} else {
					itemObjectDTO.qty = 1;
				}
				orderObject.accessorialItems.push(itemObjectDTO);
			}
		}

		orderObject.order = Object.assign({}, this.order);
		orderObject.orderTemplateId = this.order.orderTemplateId;

		if (orderObject.order.readyTimeFrom && orderObject.order.selectedPickupReady !== 'now') {
			orderObject.order.readyTimeFrom = this.getOffsetDifference(orderObject.order.readyTimeFrom, orderObject.order.pickupTimeOffset);
		} else if (orderObject.order.readyTimeFrom) {
			orderObject.order.readyTimeFrom = orderObject.order.readyTimeFrom;
		}

		if (orderObject.order.dueTimeFrom && orderObject.order.selectedDeliveryReady !== 'now' && orderObject.order.selectedPickupReady !== 'now') {
			orderObject.order.dueTimeFrom = this.getOffsetDifference(orderObject.order.dueTimeFrom, orderObject.order.deliveryTimeOffset);
		} else if (orderObject.order.dueTimeFrom) {
			orderObject.order.dueTimeFrom = orderObject.order.dueTimeFrom;
		}

		return orderObject;
	}

	/**
	 * @api {post} /v2/Orders/Customer/:custId/Barcode/:barcodeTranslationId Create Order From Barcode
	 * @apiName createOrderFromBarcode
	 * @apiParam {boolean} [placeOrderAutomatically] Place Order Automatically
	 * @apiGroup Orders
	 * @apiVersion 20.0.0
	 */
	createOrderFromBarcode(barcode, custId, barcodeTranslationId, placeOrderAutomatically = false) {
		let params: HttpParams = new HttpParams();
		params = params.append('placeOrderAutomatically', placeOrderAutomatically);
		params = params.append('bypassInvoiceAgingWarning', true);
		return this._http.post<any>(`${environment.apiUrl}/v2/Orders/Customer/${custId}/Barcode/${barcodeTranslationId}`, `"${barcode}"`, {params});
	}

	deleteNextOrderAction() {
		this.nextOrderAction = null;
	}

	/**
	 * @api {put} /v2/InetOrders/:id Edit Order
	 * @apiName editOrder
	 * @apiParam {boolean} [bypassInvoiceAgingWarning] Skip Invoice Aging Warning
	 * @apiParam {boolean} [preserveToTimes]
	 * @apiGroup InetOrders
	 * @apiVersion 20.0.0
	 */
	editOrder(data, id, preserveToTimes = false) {
		let params: HttpParams = new HttpParams();
		params = params.append('bypassInvoiceAgingWarning', true);
		params = params.append('preserveToTimes', preserveToTimes);
		return this._http.put<any>(`${environment.apiUrl}/v2/InetOrders/${id}`, data, {params});
	}

	/**
	 * @api {post} /v2/Orders/:id/ShippingLabels/:shippingLabelId/Email Email Shipping Label
	 * @apiName emailShippingLabel
	 * @apiParam {boolean} [usePieceCount] Use Piece Count
	 * @apiGroup Orders
	 * @apiVersion 20.0.0
	 */
	emailShippingLabel(id, shippingLabelId, emailAddresses, includePieces?) {
		let params: HttpParams = new HttpParams();
		if (includePieces) {
			params = params.append('usePieceCount', includePieces);
		}
		return this._http.post<any>(`${environment.apiUrl}/v2/Orders/${id}/ShippingLabels/${shippingLabelId}/Email`, emailAddresses, {params})
			.pipe(map(res => res));
	}

	firstCancelOrderAlert(order) {
		return new Observable((observer) => {
			if (order.isParent) {
				const dialogRef = this._dialog.open(ModalComponent, {
					data: {
						title: 'Alert',
						message: `Order ${order.id} has associated continuation orders.  Are you sure you want to cancel this order?`,
						showCancel: true,
						cancelText: 'No',
						okText: 'Yes'
					}
				});
				dialogRef.afterClosed().subscribe((result) => {
					if (result) {
						observer.next(true);
						observer.complete();
					} else {
						observer.next(false);
						observer.complete();
					}
				});
			} else {
				const dialogRef = this._dialog.open(ModalComponent, {
					data: {
						title: 'Alert',
						message: `You are about to cancel this order.  Would you like to proceed?`,
						showCancel: true,
						cancelText: 'No',
						okText: 'Yes'
					}
				});
				dialogRef.afterClosed().subscribe((result) => {
					if (result) {
						observer.next(true);
						observer.complete();
					} else {
						observer.next(false);
						observer.complete();
					}
				});
			}
		});
	}

	/**
	 * @api {get} /v2/Orders/AllowedAccessorials/:orderTypeId/:rateChartId Get Allowed Accessorials
	 * @apiName getAvailableItems
	 * @apiGroup Orders
	 * @apiVersion 20.0.0
	 */
	getAvailableItems(orderTypeId, rateChartId) {
		return this._http.get<any>(`${environment.apiUrl}/v2/Orders/AllowedAccessorials/${orderTypeId}/${rateChartId}`);
	}

	getAvailableOrderTypes() {
		return this.availableOrderTypes;
	}

	/**
	 * @api {get} /v2/Orders/ShippingLabels Get Available Shipping Labels
	 * @apiName getAvailableShippingLabels
	 * @apiGroup Orders
	 * @apiVersion 20.0.0
	 */
	getAvailableShippingLabels() {
		return this._http.get<any>(`${environment.apiUrl}/v2/Orders/ShippingLabels`);
	}

	getCurrentOrder() {
		return this.order;
	}

	/**
	 * @api {get} /v2/Orders/:id/ShippingLabels/:procedureId/PDF Get Shipping Label
	 * @apiName getLabelByProcedureId
	 * @apiParam {boolean} [usePieceCount] Use Piece Count
	 * @apiGroup Orders
	 * @apiVersion 20.0.0
	 */
	getLabelByProcedureId(id, procedureId, includePieces?) {
		let params: HttpParams = new HttpParams();
		if (includePieces) {
			params = params.append('usePieceCount', includePieces);
		}
		return this._http.get<Blob>(`${environment.apiUrl}/v2/Orders/${id}/ShippingLabels/${procedureId}/PDF`, {responseType: 'blob' as 'json', params});
	}

	getLastPlacedOrderId() {
		return this.lastPlacedOrderId;
	}

	getNextOrderAction() {
		return this.nextOrderAction;
	}

	getOffsetDifference(time, destOffset) {
		// This assumes a negative offset and will only work in the Americas
		// Assumes destOffset is always negative
		const selectedDate = new Date(time);
		const finalOffset = (-selectedDate.getTimezoneOffset() - destOffset) * 60000;
		return time + finalOffset;
	}

	/**
	 * @api {get} /v3/Orders/:id/true Get Order
	 * @apiName getOrderById
	 * @apiGroup Orders
	 * @apiVersion 20.0.0
	 */
	getOrderById(id) {
		return this._http.get<any>(`${environment.apiUrl}/v3/Orders/${id}/true`);
	}

	/**
	 * @api {get} /v2/Distance/:custId/:typeId/:date Get Order Distance
	 * @apiName getOrderDistance
	 * @apiParam {string} [origin] Origin
	 * @apiParam {string} [destination] Destination
	 * @apiGroup Distance
	 * @apiVersion 20.0.0
	 */
	getOrderDistance(origin, destination, custId, typeId, date) {
		let params: HttpParams = new HttpParams();
		params = params.append('origin', origin);
		params = params.append('destination', destination);
		return this._http.get<any>(`${environment.apiUrl}/v2/Distance/${custId}/${typeId}/${date}`, {params});
	}

	/**
	 * @api {get} /v2/Orders/:id/Items Get Order Items
	 * @apiName getOrderItems
	 * @apiGroup Orders
	 * @apiVersion 20.0.0
	 */
	getOrderItems(id) {
		return this._http.get<any>(`${environment.apiUrl}/v2/Orders/${id}/Items`);
	}

	/**
	 * @api {get} /v2/Orders/${id}/Parcels Get Order Parcels
	 * @apiName getOrderParcelsById
	 * @apiGroup Orders
	 * @apiVersion 20.0.0
	 */
	getOrderParcelsById(id) {
		return this._http.get<any>(`${environment.apiUrl}/v2/Orders/${id}/Parcels`);
	}

	getOrderQueue() {
		const orderQueue = localStorage.getItem('Rapidship:orderQueue');
		if (orderQueue && JSON.parse(orderQueue)) {
			this.orderQueue = JSON.parse(orderQueue);
			this.orderQueue$.next(this.orderQueue);
		}
	}

	/**
	 * @api {post} /v2/Orders/RatesWithTimeWindows Get order Types
	 * @apiName getOrderTypes
	 * @apiParam {boolean} [preserveToTimesForCurrentOrderType]
	 * @apiGroup Orders
	 * @apiVersion 20.0.0
	 */
	getOrderTypes(orderData, preserveToTimesForCurrentOrderType = false) {
		let params: HttpParams = new HttpParams();
		params = params.append('preserveToTimesForCurrentOrderType', preserveToTimesForCurrentOrderType);
		return this._http.post<any>(`${environment.apiUrl}/v2/Orders/RatesWithTimeWindows`, orderData, {params});
	}

	/**
	 * @api {get} /v2/Customers/:id/ParcelTypes Get Parcel Types
	 * @apiName getParcelTypes
	 * @apiGroup Customers
	 * @apiVersion 20.0.0
	 */
	getParcelTypes(id) {
		return this._http.get<any>(`${environment.apiUrl}/v2/Customers/${id}/ParcelTypes`);
	}

	/**
	 * @api {get} /v2/Orders/:id/Receipt Get Receipt
	 * @apiName getReceipt
	 * @apiGroup Orders
	 * @apiVersion 20.0.0
	 */
	getReceipt(id) {
		return this._http.get<any>(`${environment.apiUrl}/v2/Orders/${id}/Receipt`);
	}

	/**
	 * @api {get} /v2/Customers/:custId/Reports/Receipt Get Receipt of all On Demand Orders in General Report
	 * @apiName getGeneralReportReceipt
	 * @apiParam {string} [beginDate] Begin Date
	 * @apiParam {string} [endDate] End Date
	 * @apiParam {string} [filterField] Filter Field
	 * @apiParam {string} [filterOperator] Filter Operator
	 * @apiParam {string} [filterText] Filter Text
	 * @apiParam {string} [detailLevel] Detail Level
	 * @apiParam {number} [top] Top records
	 * @apiGroup Reports
	 * @apiVersion 20.0.0
	 */
	getGeneralReportReceipt(custId, beginDate, endDate, quantity, filterField, filterType, filtertext) {
		let params: HttpParams = new HttpParams();
		if (beginDate) {
			params = params.append('beginDate', beginDate);
		}
		if (endDate) {
			params = params.append('endDate', endDate);
		}
		if (filterField) {
			params = params.append('filterField', filterField);
		}
		if (filterType) {
			params = params.append('filterOperator', filterType);
		}
		if (filtertext) {
			params = params.append('filterText', filtertext);
		}
		params = params.append('top', quantity);
		return this._http.get<any>(`${environment.apiUrl}/v2/Customers/${custId}/Reports/Receipt`, {params});
	}

	/**
	 * @api {post} /v2/Orders/Recent Get Recent Orders
	 * @apiName getRecentOrders
	 * @apiParam {number} [pageSize] Page Size
	 * @apiGroup Orders
	 * @apiVersion 20.0.0
	 */
	getRecentOrders(searchCriteria, count, customerId) {
		let params: HttpParams = new HttpParams();
		params = params.append('numToGet', count);
		if (customerId) {
			params = params.append('customerId', customerId);
		}
		return this._http.post<any>(`${environment.apiUrl}/v2/Orders/Recent`, JSON.stringify(searchCriteria), {params});
	}

	/**
	 * @api {get} /v2/OrderTypeReduced/:orderTypeId Get Reduced Order Type
	 * @apiName getReducedOrderTypeById
	 * @apiGroup OrderTypeReduced
	 * @apiVersion 20.0.0
	 */
	getReducedOrderTypeById(orderTypeId) {
		return this._http.get<any>(`${environment.apiUrl}/v2/OrderTypeReduced/${orderTypeId}`);
	}

	/**
	 * @api {post} /v2/Orders/ItemizedDisplayCharges Get Itemized Display Charges
	 * @apiName getSurchargesAndTaxes
	 * @apiParam {boolean} [preserveToTimes]
	 * @apiGroup Orders
	 * @apiVersion 20.0.0
	 */
	getSurchargesAndTaxes(orderDTO, preserveToTimes = false) {
		let params: HttpParams = new HttpParams();
		params = params.append('preserveToTimes', preserveToTimes);
		return this._http.post<any>(`${environment.apiUrl}/v2/Orders/ItemizedDisplayCharges`, orderDTO, {params});
	}

	/**
	 * @api {get} /v2/TimeZoneOffsets/:postalCode/:calcDate Get Time Zone Offset
	 * @apiName getTimeZoneOffset
	 * @apiGroup TimeZoneOffsets
	 * @apiVersion 20.0.0
	 */
	getTimeZoneOffset(postalCode, calcDate) {
		return this._http.get<any>(`${environment.apiUrl}/v2/TimeZoneOffsets/${postalCode}/${calcDate}`);
	}

	getUnplacedOrderData() {
		return this.unplacedOrderData;
	}

	handleCancelOrder(order) {
		return new Promise<void>((resolve, reject) => {
			let continuationBypass: boolean;
			let routeStopsBypass1: boolean;
			let routeStopsBypass2: boolean;
			this.firstCancelOrderAlert(order).subscribe((firstResponse: boolean) => {
				if (firstResponse) {
					continuationBypass = firstResponse;
					this.secondCancelOrderAlert(order).subscribe((secondResponse: boolean) => {
						if (secondResponse !== null) {
							routeStopsBypass1 = secondResponse;
							this.thirdCancelOrderAlert().subscribe((thirdResponse: boolean) => {
								if (thirdResponse !== null) {
									routeStopsBypass2 = thirdResponse;
									this.cancelOrder(order.id, continuationBypass, routeStopsBypass1, routeStopsBypass2).then(() => {
										resolve();
									}, () => {
										reject();
									});
								} else {
									// Make WS call
									this.cancelOrder(order.id, continuationBypass, routeStopsBypass1, routeStopsBypass2).then(() => {
										resolve();
									}, () => {
										reject();
									});
								}
							});
						} else {
							// Make WS call
							this.cancelOrder(order.id, continuationBypass, routeStopsBypass1, routeStopsBypass2).then(() => {
								resolve();
							}, () => {
								reject();
							});
						}
					});
				} else {
					// Do nothing
					console.warn('User canceled');
					reject();
				}
			}, (error) => {
				console.warn(error);
				reject();
			});
		});
	}

	prefixCommentsWithContact(comments, contact, prefix) {
		let updatedComments = '';

		if (contact && contact.length > 0) {
			updatedComments += `${prefix} ${contact}`;
		}

		if (comments && comments.length > 0) {
			if (updatedComments.length > 0) {
				updatedComments += ': ';
			}
			updatedComments += comments;
		}

		return updatedComments;
	}

	removeOrderFromQueue(index) {
		this.orderQueue.splice(index, 1);
		this.saveOrderQueue();
	}

	resetCurrentOrder() {
		this.resetOrder$.emit(true);
	}

	saveOrderQueue() {
		this.orderQueue$.next(this.orderQueue);
		localStorage.setItem('Rapidship:orderQueue', JSON.stringify(this.orderQueue));
	}

	secondCancelOrderAlert(order) {
		return new Observable((observer) => {
			if (order.hasAssociatedRouteStops) {
				const dialogRef = this._dialog.open(ModalComponent, {
					data: {
						title: 'Alert',
						message: `Order ${order.id} has associated route stops.  Are you sure you want to cancel this order?`,
						showCancel: true,
						cancelText: 'No',
						okText: 'Yes'
					}
				});
				dialogRef.afterClosed().subscribe((result) => {
					if (result) {
						observer.next(true);
						observer.complete();
					} else {
						observer.next(false);
						observer.complete();
					}
				});
			} else {
				observer.next(null);
				observer.complete();
			}
		});
	}

	setAvailableOrderTypes(data) {
		this.availableOrderTypes = data;
	}

	setCurrentOrder(data) {
		this.order = data;
	}

	setLastPlacedOrderId(id) {
		this.lastPlacedOrderId = id;
	}

	setNextOrderAction(action: string, id) {
		this.nextOrderAction = {
			action: action,
			id: id
		};

 		const token = this.user.token;
		const message = this._customPageService.processContextData('placeOrderIframe', action, id, token);
		this._customPageService.iframeMessageSource.next({iframeId: 'placeOrderIframe', message, targetOrigin: '*'});
 	}

	setUnplacedOrderData(data) {
		this.unplacedOrderData = data;
	}

	thirdCancelOrderAlert() {
		return new Observable((observer) => {
			const dialogRef = this._dialog.open(ModalComponent, {
				data: {
					title: 'Alert',
					message: `Do you want to also cancel the associated route stops?`,
					showCancel: true,
					cancelText: 'No',
					okText: 'Yes'
				}
			});
			dialogRef.afterClosed().subscribe((result) => {
				if (result) {
					observer.next(true);
					observer.complete();
				} else {
					observer.next(false);
					observer.complete();
				}
			});
		});
	}

	updateOrderInQueue(order, index) {
		this.orderQueue[index] = order;
		this.saveOrderQueue();
	}
}
